import React, { useEffect } from 'react';
import type { RefObject } from 'react';

import type {
	BackendExperiment,
	ProductEdition,
	SearchExperimentLayer,
} from '@atlassian/search-experiment';

import { LoadableLazy } from '@confluence/loadable';

import type { AdvancedSearchResultType } from '../../AdvancedSearch-types.ts';
import type { AdvancedAGGSearchQueryAbTest } from '../../gql/__types__/AdvancedSearchAGGTypes.ts';
import { SearchResult } from '../Item/SearchResult.tsx';
import { ResultCountDisplay } from '../../ResultCountDisplay/ResultCountDisplay.tsx';
import { useSearchInputContext } from '../../SearchInputProvider.tsx';
import { useAnalytics } from '../../AnalyticsProvider.tsx';
import { PAGE_SIZE } from '../../AdvancedSearch-constants.ts';

import { SearchPagination } from './SearchPagination.tsx';

interface SearchResultListResultsProps {
	results: AdvancedSearchResultType[];
	numResults: number;
	totalCount?: number | null;
	hasNextPage: boolean;
	loadMore: () => void;
	resultListRef?: RefObject<HTMLElement>;
	isStale: boolean;
	queryState: string;
	abTest: AdvancedAGGSearchQueryAbTest | BackendExperiment | null;
	experimentLayers?: SearchExperimentLayer[];
	productEdition?: ProductEdition;
}

const SearchResultListNoResults = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SearchResultListNoResults" */
				'./SearchResultListNoResults.tsx'
			)
		).SearchResultListNoResults,
});

export const SearchResultListResults = ({
	results,
	numResults,
	totalCount,
	hasNextPage,
	loadMore,
	isStale,
	queryState,
	abTest,
	experimentLayers,
	productEdition,
}: SearchResultListResultsProps) => {
	const { sessionHasAppliedFilters, clearFilters } = useSearchInputContext();
	const { fireAnalytics } = useAnalytics();

	useEffect(() => {
		if (!isStale && results.length) {
			fireAnalytics({
				type: 'sendTrackEvent',
				action: 'shown',
				actionSubject: 'advancedSearchResults',
				additionalAttributes: {
					queryState,
					numResults,
					abTest,
					numberOfResultsShown: numResults,
					noResultsFetched: numResults === 0,
					numberOfDocs: numResults,
					pageNumber: numResults ? Math.ceil(numResults / PAGE_SIZE) : 1,
					backendExperiments: {
						experimentLayers,
					},
					productEdition,
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isStale, results]);

	return results.length ? (
		<div data-testid="post-query-with-results-test">
			<ResultCountDisplay numberOfResults={numResults} totalCount={totalCount} />
			<ul data-testid="advanced-search-resultlist">
				{results.map((result, idx) => (
					<SearchResult key={result.url} searchResult={result} searchResultIdx={idx} />
				))}
			</ul>
			<SearchPagination loading={false} hasNextPage={hasNextPage} loadMore={loadMore} />
		</div>
	) : (
		<div data-testid="post-query-no-results-test">
			<SearchResultListNoResults
				clearFilters={clearFilters}
				withFiltersSelected={sessionHasAppliedFilters}
				queryState={queryState}
				abTest={abTest}
				experimentLayers={experimentLayers}
			/>
		</div>
	);
};
